import React, { useCallback, useRef, useState } from 'react';
import {
  Banner,
  BlockStack,
  Button,
  Card,
  FormLayout,
  Modal,
  Select,
  Text,
  TextField,
  Toast,
  InlineStack
} from '@shopify/polaris';
import { ordersService } from '../../../services/ordersService';
import { generationStoriesService } from '../../../services/generationStoriesService';
import { isAddonLine } from '../../../utils/orderAddons';

const MIN_REASON_LENGTH = 10;

const currentStorySlug = lineItem =>
  lineItem?.properties?.find(property => property.name === 'Story Slug')?.value || 'N/A';

// Minimal edit mode for orders past the staged-edit window: the only change
// still possible is swapping a line item's story, applied immediately.
export default function StorySwapMode({ order, onExit, onReload }) {
  const [stories, setStories] = useState([]);
  const [storiesLoaded, setStoriesLoaded] = useState(false);
  const [swapLine, setSwapLine] = useState(null);
  const [form, setForm] = useState({ story_id: '', kid_name: '', reason: '' });
  const [applying, setApplying] = useState(false);
  const [toast, setToast] = useState(null);
  const applyingRef = useRef(false);

  const storyLines = (order.line_items || []).filter(item => !isAddonLine(item));

  const loadStories = useCallback(async () => {
    if (storiesLoaded) return;
    try {
      const all = await generationStoriesService.getAllStories();
      setStories(all || []);
    } catch (error) {
      setToast({ content: `Failed to load stories: ${error.message}`, error: true });
    } finally {
      setStoriesLoaded(true);
    }
  }, [storiesLoaded]);

  const openSwap = lineItem => {
    setSwapLine(lineItem);
    setForm({
      story_id: lineItem.properties?.find(property => property.name === 'Story ID')?.value || '',
      kid_name: lineItem.properties?.find(property => property.name === 'Kid Name')?.value || '',
      reason: ''
    });
    loadStories();
  };

  const closeSwap = () => {
    setSwapLine(null);
    setForm({ story_id: '', kid_name: '', reason: '' });
  };

  const reasonTooShort = form.reason.trim().length < MIN_REASON_LENGTH;
  const applyDisabled = !form.story_id || !form.reason.trim() || reasonTooShort;

  const handleApply = async () => {
    if (!swapLine || applyingRef.current || applyDisabled) return;
    applyingRef.current = true;
    setApplying(true);
    try {
      const updateData = {
        story_id: form.story_id,
        reason: form.reason.trim()
      };
      if (form.kid_name && form.kid_name.trim()) {
        updateData.kid_name = form.kid_name.trim();
      }
      await ordersService.updateLineItemStory(order.order_id, swapLine.id, updateData);
      setToast({ content: 'Line item story updated successfully', error: false });
      closeSwap();
      await onReload();
    } catch (error) {
      setToast({ content: `Failed to update line item story: ${error.message}`, error: true });
    } finally {
      applyingRef.current = false;
      setApplying(false);
    }
  };

  return (
    <BlockStack gap="400">
      <Banner tone="info" title={`Editing order #${order.order_id}`}>
        <p>Only story swaps are available at this stage.</p>
      </Banner>

      {storyLines.map(lineItem => (
        <Card key={lineItem.id} sectioned>
          <BlockStack gap="200">
            <InlineRow lineItem={lineItem} onSwap={() => openSwap(lineItem)} />
          </BlockStack>
        </Card>
      ))}

      <InlineStack>
        <Button onClick={onExit}>Cancel</Button>
      </InlineStack>

      <Modal
        open={Boolean(swapLine)}
        onClose={closeSwap}
        title="Change story"
        primaryAction={{
          content: 'Apply',
          onAction: handleApply,
          loading: applying,
          disabled: applyDisabled
        }}
        secondaryActions={[{ content: 'Cancel', onAction: closeSwap }]}
      >
        <Modal.Section>
          <FormLayout>
            <Banner tone="warning">
              <p>
                Changing the story resets the line item status to new and clears any existing
                generation. This action is logged for audit purposes.
              </p>
            </Banner>

            {swapLine && (
              <Text variant="bodySm" tone="subdued">
                Current story: {currentStorySlug(swapLine)}
              </Text>
            )}

            <Select
              label="New story"
              options={[
                { value: '', label: 'Select a story' },
                ...stories.map(story => ({
                  value: story._id,
                  label: `${story.title} (${story.slug}) - ${story.language || 'N/A'}`
                }))
              ]}
              value={form.story_id}
              onChange={value => setForm(current => ({ ...current, story_id: value }))}
              helpText="Select the new story for this line item"
            />

            <TextField
              label="Kid name (optional)"
              value={form.kid_name}
              onChange={value => setForm(current => ({ ...current, kid_name: value }))}
              placeholder="Enter kid name to update"
              helpText="Leave empty to keep the current kid name"
              autoComplete="off"
            />

            <TextField
              label="Reason"
              value={form.reason}
              onChange={value => setForm(current => ({ ...current, reason: value }))}
              multiline={3}
              placeholder="Explain why this change is needed (minimum 10 characters)"
              helpText={`Required: ${form.reason.length}/10 characters minimum`}
              error={
                form.reason.trim() && form.reason.length < MIN_REASON_LENGTH
                  ? 'Reason must be at least 10 characters'
                  : undefined
              }
              autoComplete="off"
            />
          </FormLayout>
        </Modal.Section>
      </Modal>

      {toast && (
        <Toast
          content={toast.content}
          error={toast.error}
          onDismiss={() => setToast(null)}
        />
      )}
    </BlockStack>
  );
}

const InlineRow = ({ lineItem, onSwap }) => (
  <BlockStack gap="200">
    <Text variant="bodyMd" fontWeight="semibold">
      {lineItem.name || 'N/A'}
    </Text>
    <Text variant="bodySm" tone="subdued">
      Price: {lineItem.price || '0.00'} - Current story: {currentStorySlug(lineItem)}
    </Text>
    <Button size="slim" onClick={onSwap}>Change story</Button>
  </BlockStack>
);
